import { Card, ErrorNote, Loading, formatDate, formatMoney } from "@corecrm/ui";
import { useClientPreview } from "../api/queries";
import type { Quote, QuoteSummary } from "../api/types";
import { Status } from "../components/bits";
import { QuoteDocument } from "../components/QuoteDocument";
import { InvoiceView } from "./Invoices";
import { RevisionNote } from "./Quote";

/** The token from `/preview#<token>`: in the fragment, so it never reaches a server log. */
export function previewToken(hash: string): string {
  return decodeURIComponent(hash.replace(/^#/, "")).trim();
}

/* Staff's "Preview as client" (UX-113): the quote or invoice exactly as the client sees it, read
   with a short-lived token from CoreCRM instead of a sign-in. Read-only: no Accept, Decline or Pay,
   and the API records nothing (no view, no Viewed status, nobody notified). */
export function PreviewPage() {
  const token = previewToken(window.location.hash);
  const found = useClientPreview(token);
  return (
    <div className="wrap preview-page" style={{ maxWidth: 960, margin: "0 auto", padding: 16 }}>
      <div className="note acc" role="status">
        <strong>Preview as client.</strong> This is what the client sees in the portal. It&rsquo;s read-only
        and nothing you do here is recorded. The link expires after 30 minutes.
      </div>
      {!token ? (
        <p className="muted">Open the preview again from CoreCRM.</p>
      ) : found.isPending ? (
        <Loading />
      ) : found.isError ? (
        <ErrorNote error={found.error} />
      ) : found.data.quote ? (
        <QuotePreview quote={found.data.quote} versions={found.data.versions} />
      ) : found.data.invoice ? (
        <InvoiceView invoice={found.data.invoice} preview />
      ) : null}
    </div>
  );
}

function QuotePreview({ quote: q, versions }: { quote: Quote; versions: QuoteSummary[] }) {
  const history = versions.filter((v) => v.id !== q.id).reverse();
  return (
    <>
      <h1 style={{ fontSize: 20 }}>
        {q.number} v{q.version_number}
      </h1>
      {q.revision_note && <RevisionNote quote={q} />}
      <QuoteDocument quote={q} />
      {history.length > 0 && (
        <Card title="Version history">
          <div className="item-list">
            {history.map((v) => (
              <div className="item" key={v.id}>
                <div className="what">
                  <strong>Version {v.version_number}</strong>
                  <span>
                    <Status status={v.status} /> · sent {formatDate(v.sent_at)} · {formatMoney(v.total)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
      <p className="faint">
        The client also sees Accept and Decline here while the quote is open, and can download the PDF.
      </p>
    </>
  );
}
